import { captureHandledException } from "src/services/sentry";

import { createWindowManager } from "../../utils/WindowManager";
import { getMoodleLoadingPage } from "./LoadingPage";

interface OpenCourseWithLoaderOptions {
  courseId: number;
  userId: number;
  courseName: string;
  goToCourse: (courseId: number, userId: number) => Promise<string>;
  t: (key: string) => string;
  onError: (title: string, message: string) => void;
}

/**
 * Opens a Moodle course in a new window with a loading spinner.
 * This function handles the popup blocker issue by opening the window synchronously
 * and then navigating to the course URL after the API call completes.
 */
export const openCourseWithLoader = async ({
  courseId,
  userId,
  courseName,
  goToCourse,
  t,
  onError,
}: OpenCourseWithLoaderOptions): Promise<void> => {
  const windowManager = createWindowManager();
  windowManager.open();
  windowManager.setContent(getMoodleLoadingPage(t("moodle.loading.title"), t("moodle.loading.message"), courseName));

  // Close popup if main window is about to navigate away (e.g., logout redirect)
  const closePopupOnUnload = () => {
    windowManager.close();
  };
  window.addEventListener("beforeunload", closePopupOnUnload);

  try {
    const url = await goToCourse(courseId, userId);

    // Remove the event listener once we have the response
    window.removeEventListener("beforeunload", closePopupOnUnload);

    if (!url) {
      windowManager.close();
      onError(t("certificate.error_enrolling_course"), t("certificate.error_try_again_later"));
    } else {
      if (windowManager.isOpen()) {
        windowManager.redirect(url);
      } else {
        console.error("Window was blocked or closed");
      }
    }
  } catch (error) {
    // Remove the event listener on error
    window.removeEventListener("beforeunload", closePopupOnUnload);

    windowManager.close();
    console.error("Error opening course:", error);
    captureHandledException(error, { component: "openCourseWithLoader", action: "openCourse", courseId, userId });
    onError(t("certificate.error"), t("certificate.error_processing_course"));
  }
};
